/**
 * Line-kilometre accounting, the TJR convention (WEEKLY_REPORT_BUILD.md §4, verified line by line against the
 * TJR export on 2026-10-16):
 *   - a fully flown production line is credited with its PLANNED length (reflight versions use the base line);
 *   - a partly flown line is credited with the flown length (never more than the planned length);
 *   - an operator override wins (km = 0 means rejected, e.g. "Diurnal out of spec, will refly");
 *   - test, comp-box and ground-check lines never count.
 * "Fully flown" is the Zdas log's completion marker, `[Comp 100%]` (decision 176, Q154; `isFullyFlown`). Teslyn
 * dropped the old 90 %-of-planned rule; the weekly generator's outputs, which used it, are reproduced in the tests
 * by passing that rule as `fullyFlown`.
 * Week and to-date totals are per block; a flight that touched two blocks contributes to both.
 */
import { parseLineNumber } from './lines.js';
import { isFullyFlown } from './zdas.js';

export interface AcceptanceOverride {
  /** Accepted km to credit for this line (0 = rejected). */
  readonly km: number;
  readonly reason?: string;
}

/** Planned length in km of each planned line, keyed by its base number (version digit 0). */
export type PlannedLengths = Readonly<Record<string, number>>;

/** Overrides keyed by the flown line number (with its version digit). */
export type Overrides = Readonly<Record<string, AcceptanceOverride>>;

/** Accepted km of one flown line: the override, else the planned length when fully flown, else the flown km. */
export function acceptedKm(
  line: number,
  flownKm: number,
  planned: PlannedLengths,
  overrides: Overrides = {},
  fullyFlown = false,
): number {
  const o = overrides[String(line)];
  if (o && typeof o.km === 'number') return o.km;
  const p = planned[String(parseLineNumber(line).base)];
  if (!p) return flownKm;
  return fullyFlown ? p : Math.min(flownKm, p);
}

export interface BlockPlan {
  /** Stable key, e.g. `Milgun_SouthEast`. */
  readonly key: string;
  readonly name: string;
  /** First digit of this block's line numbers. */
  readonly digit: string;
  /** Planned production km for the block (traverse + tie). */
  readonly plannedKm: number;
  readonly plannedLen: PlannedLengths;
}

export interface FlightLines {
  readonly day: number;
  readonly flt: number;
  readonly week: number;
  /** Flown km per production line number (version digit included). */
  readonly lines: Readonly<Record<string, number>>;
  /** Zdas completion marker per line (`ZdasFlight.completion`); a line without one is not fully flown. */
  readonly completion?: Readonly<Record<string, string>>;
}

export interface RejectedLine {
  readonly line: number;
  readonly reason: string;
}

export interface WeekTotals {
  readonly week: number;
  readonly weekKm: Readonly<Record<string, number>>;
  readonly todateKm: Readonly<Record<string, number>>;
  readonly weekLines: Readonly<Record<string, readonly number[]>>;
  readonly todateLines: Readonly<Record<string, readonly number[]>>;
  /** Overridden lines flown in this week, in flight order (a line reflown twice appears twice). */
  readonly rejected: readonly RejectedLine[];
  readonly weekTotalKm: number;
}

export interface WeekTotalsOptions {
  readonly overrides?: Overrides;
  /**
   * Whether a flown line counts as fully flown. Default: its Zdas marker is `Comp 100%` (decision 176). The tests
   * pass the old weekly generator's rule here to reproduce its printed figures.
   */
  readonly fullyFlown?: (line: number, flownKm: number, plannedKm: number, marker: string | null) => boolean;
}

/** Week and to-date km per block for one ISO week, from every flight up to and including that week. */
export function weekTotals(
  week: number,
  flights: readonly FlightLines[],
  blocks: readonly BlockPlan[],
  options: WeekTotalsOptions = {},
): WeekTotals {
  const overrides = options.overrides ?? {};
  const fullyFlown = options.fullyFlown ?? ((_l, _f, _p, marker) => isFullyFlown(marker));
  const byDigit = new Map(blocks.map((b) => [b.digit, b]));
  const weekKm: Record<string, number> = {};
  const todateKm: Record<string, number> = {};
  const weekLines: Record<string, Set<number>> = {};
  const todateLines: Record<string, Set<number>> = {};
  for (const b of blocks) {
    weekKm[b.key] = 0;
    todateKm[b.key] = 0;
    weekLines[b.key] = new Set();
    todateLines[b.key] = new Set();
  }
  const rejected: RejectedLine[] = [];
  const ordered = [...flights].filter((f) => f.week <= week).sort((a, b) => a.day - b.day || a.flt - b.flt);
  for (const f of ordered) {
    for (const [key, flown] of Object.entries(f.lines)) {
      const n = Number(key);
      const parsed = parseLineNumber(n);
      if (!parsed.production || parsed.blockDigit === null) continue;
      const block = byDigit.get(parsed.blockDigit);
      if (!block) continue;
      const p = block.plannedLen[String(parsed.base)] ?? 0;
      const marker = f.completion?.[key] ?? null;
      const km = acceptedKm(n, flown, block.plannedLen, overrides, fullyFlown(n, flown, p, marker));
      if (overrides[String(n)] && f.week === week) {
        rejected.push({ line: n, reason: overrides[String(n)]?.reason ?? '' });
      }
      todateLines[block.key]!.add(n);
      todateKm[block.key]! += km;
      if (f.week === week) {
        weekLines[block.key]!.add(n);
        weekKm[block.key]! += km;
      }
    }
  }
  const sorted = (s: Record<string, Set<number>>): Record<string, number[]> =>
    Object.fromEntries(Object.entries(s).map(([k, v]) => [k, [...v].sort((a, b) => a - b)]));
  return {
    week,
    weekKm,
    todateKm,
    weekLines: sorted(weekLines),
    todateLines: sorted(todateLines),
    rejected,
    weekTotalKm: Object.values(weekKm).reduce((a, b) => a + b, 0),
  };
}

export type BlockStatus = 'Completed' | 'In Progress' | 'Not Started';

export interface StatusRow {
  readonly block: string;
  readonly pct: string;
  readonly flown: string;
  readonly remaining: string;
  readonly status: BlockStatus;
}

// One formatter: `toLocaleString` makes an Intl object on every call (tens of microseconds each), and the map's line
// tooltips format thousands of lengths in one pass.
const KM_FORMAT = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Thousands-separated with two decimals, as the report prints km. */
export function formatKm(km: number): string {
  return KM_FORMAT.format(km);
}

/**
 * Project Status table rows for the weekly report (to-date state at the end of the week). Percentage complete is
 * capped at 100.0 %; flown and remaining are not (remaining never goes below 0).
 */
export function statusRows(
  blocks: readonly BlockPlan[],
  todateKm: Readonly<Record<string, number>>,
): StatusRow[] {
  return blocks.map((b) => {
    const flown = todateKm[b.key] ?? 0;
    const remaining = Math.max(0, b.plannedKm - flown);
    // an over-flown block (reflights, run-on) reads 100.0 %, never more (MB, 18 Oct 2026; the weekly generator)
    const pct = Math.min(100, (100 * flown) / b.plannedKm);
    const status: BlockStatus =
      remaining <= 0.005 * b.plannedKm ? 'Completed' : flown > 0 ? 'In Progress' : 'Not Started';
    return {
      block: b.name,
      pct: `${pct.toFixed(1)}%`,
      flown: `${formatKm(flown)} L/Km`,
      remaining: `${formatKm(remaining)} L/Km`,
      status,
    };
  });
}
